import { debounce, isEqual, uniqBy } from 'lodash-es';
import { reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import type { Ref } from 'vue';
import type { AggregationOption, ProductsQuery } from '@robustastudio/e-commerce/common';
import type { Aggregation } from '~/graphql-types.gen';
import {
  isPriceProps,
  isSelectProps,
  isToggleProps,
  type AllFacets,
  type IHiddenFacet,
  type IPriceFacet,
  type ISelectFacet,
  type IToggleFacet,
} from '~/types/facets';
import type { LocationQuery } from '#vue-router';

const whiteListedParams: RegExp[] = [/category_parent_id/, /q/, /sort/, /utm_/, /fbclid/, /gclid/, /page/];

export enum FacetType {
  Price = 'price',
  Toggle = 'toggle',
  Hidden = 'hidden',
  Select = 'select',
}

interface FacetComposableOptions {
  exclude?: string[];
  only?: string[];
  whiteListedParams?: RegExp[];
}

export type Facets = Map<string, AllFacets>;
/**
 * A composable function that provides reactive state and functions for managing facets and filters.
 *
 * @param {Ref<NonNullable<ProductsQuery["connection"]>["aggregations"]>} aggregations - The reactive variable that holds the aggregations data returned from the API.
 * @param {FacetComposableOptions} [opts] - An optional object that can be used to configure the behavior of the composable. See {@link FacetComposableOptions}.
 * @returns {FacetComposable} An object containing the reactive state and functions for managing facets and filters. See {@link FacetComposable}.
 */
export function useAppFacets(
  aggregations: Ref<NonNullable<ProductsQuery['connection']>['aggregations']>,
  opts?: FacetComposableOptions,
) {
  const exclude = opts?.exclude ?? [];

  const facets = ref<Facets>(new Map());

  const route = useRoute();
  const { currentRoute, replace } = useRouter();

  // exception for facets prices
  // predefine price facets limits
  const debouncedOnUpdateFacets = debounce(onUpdateFacets, 500);
  const price = reactive<{
    from: number | undefined;
    to: number | undefined;
  }>({
    from: undefined,
    to: undefined,
  });

  watch(
    aggregations,
    (value, beforeValue) => {
      if (!value?.length) {
        return;
      }

      /**
       * A way to determine that there is an actual change in the values of the aggregations
       */
      if (value && beforeValue && isEqual(value, beforeValue)) {
        return;
      }

      /**
       * Check if this is the first time we got the aggregations
       */
      const isFirstTime = !beforeValue?.length && value?.length;

      /**
       *
       * This is price lock flag to hold price aggregation when we got zero results
       *
       *
       * corner case #1: when we get a zero result that causes the filters to reset to the base state, so this triggers another fetch request that returns a collection of products with filters
       * that filters get mapped to our selections so it resets the filter an repeat this all over again causing a loop
       * so it order to mitigate that , we lock the price facet incase of the out of range result appeared
       *
       *
       */
      const priceLock =
        beforeValue?.length && !value?.length ? beforeValue?.find(item => item?.attribute_code === 'price') : undefined;

      if (!price.to) {
        price.from = 0;
        price.to = Number(
          aggregations.value
            ?.find(option => option?.attribute_code === 'price')
            ?.options?.at(-1)
            ?.value?.split('_')
            ?.at(-1),
        );
      }

      facets.value = toNonNullable(priceLock ? [priceLock] : value)
        .filter(aggregation => {
          return (
            !exclude.includes(aggregation?.attribute_code) &&
            (opts?.only && opts?.only.length > 0 ? opts?.only.includes(aggregation?.attribute_code) : true)
          );
        })
        .reduce<Facets>((accu, aggregation) => {
          const facet = mapAggregation(facets.value)(
            /**
             * lock the price facet range between from and to values that comes from the first listing query
             * so we prevent the slider from being changing depending on the value we select that might end us up to a loop
             *
             */
            aggregation.attribute_code === 'price' && price.to
              ? {
                  price: [0, price.to],
                }
              : {},
            isFirstTime ? route.query : {},
          )(aggregation);

          return accu.set(aggregation.attribute_code, facet);
        }, new Map());
    },
    {
      immediate: true,
      deep: true,
    },
  );

  // side effects
  watch(
    facets,
    (newValues, oldValues) => {
      debouncedOnUpdateFacets(newValues, oldValues);
    },
    {
      deep: true,
    },
  );
  const whiteList: RegExp[] = [...whiteListedParams, ...(opts?.whiteListedParams || [])];

  const productQueryFilters = computed(() => {
    // if there are no facets and no query, return an empty object
    if (!facets.value.size && !route.query) {
      return {};
    }
    type FacetQuery = Record<
      string,
      {
        in?: string[];
        eq?: string;
        from?: number;
        to?: number;
      }
    >;

    const initialRecord: FacetQuery = {};

    if (!facets.value.size && Object.keys(route.query).length !== 0) {
      const query = route.query;
      const keys = Object.keys(query);
      const filteredQuery = keys.reduce<FacetQuery>((obj, key) => {
        // if its exited in the white list ignore it
        if (whiteList.some(regex => regex.test(key))) {
          return obj;
        }

        // if key includes _from or _to without both of them ignore it.
        if (key.includes('_from') && !keys.includes(key.replace('_from', '_to'))) {
          return obj;
        }

        // if it includes _to and _from then it's a price range
        if (key.includes('_from')) {
          return {
            ...obj,
            price: {
              ...(obj.price || {}),
              from: Number(query[key]?.toString()),
            },
          };
        }

        if (key.includes('_to')) {
          return {
            ...obj,
            price: {
              ...(obj.price || {}),
              to: Number(query[key]?.toString()),
            },
          };
        }

        return {
          ...obj,
          [key]: {
            ...(Array.isArray(query[key])
              ? { in: [...(query[key]?.map(val => val?.toString() ?? '') ?? [])] }
              : { in: [query[key]?.toString() || ''] }),
          },
        };
      }, initialRecord);
      return filteredQuery;
    }
    const facetsArray = Array.from(facets.value.values());

    return facetsArray.reduce((acc, facet) => {
      const value = facet.value;
      if (!(Array.isArray(value) ? value.length : value)) {
        return acc;
      }

      // Skip including price if it already has the value of the existing min/max range.
      if (isPriceProps(facet) && value?.toString() === facet.options.toString()) {
        return acc;
      }

      if (isPriceProps(facet) && Array.isArray(value)) {
        const value = facet.value;

        acc.price = {
          from: value?.[0],
          to: value?.[1],
        };

        /**
         * if price range is already defined
         * set the value so the price facet can be locked to a certain value
         */
        if (!price.to) {
          price.from = 0;
          price.to = Number(lastItem(facet.options));
        }
        return acc;
      }

      if (isSelectProps(facet) && Array.isArray(value) && value.length) {
        const value = facet.value;

        acc[facet.code] = {
          in: uniqBy(value, 'value').map(option => option.value),
        };

        return acc;
      }

      if (isToggleProps(facet)) {
        acc[facet.code] = { eq: facet.options?.value ?? '0' };
      }

      // if (!Array.isArray(value)) {
      //   acc[facet.code] = { in: [value?.value?.toString() ?? ''] };
      // }

      return acc;
    }, initialRecord);
  });

  /**
   * @description On update facets
   * @param param
   */
  function onUpdateFacets(newValue: Facets, oldFacets: Facets) {
    let _whiteList = whiteList;
    // Initiating or recreating the facets
    if (!oldFacets.size) {
      _whiteList = [...whiteList];
    }

    const currentQuery = currentRoute.value?.query || {};

    const query = { ...fromFacetToUrlParams(newValue) };

    const filteredQuery = Object.keys(currentQuery)
      .filter(query => _whiteList.some(regex => regex.test(query)))
      .reduce((obj, key) => {
        return {
          ...obj,
          [key]: currentQuery && currentQuery[key],
        };
      }, {});

    replace({
      query: {
        ...filteredQuery,
        ...query,
      },
    }).catch(err => {
      if (err.name !== 'NavigationDuplicated') {
        // throw err;
      }
    });
  }

  // facet functions
  function removeItem({ code, value }: { code: string; value: string | null }): Facets {
    const facet = facets.value.get(code) as AllFacets;
    if (isPriceProps(facet)) {
      return facets.value.set(code, {
        ...facet,
        value: [0, Number(facet.options[facet.options.length - 1])],
      });
    }

    if (isSelectProps(facet)) {
      return facets.value.set(code, {
        ...facet,
        value: facet.value?.filter(option => option.value !== value),
      });
    }

    if (isToggleProps(facet)) {
      return facets.value.set(code, {
        ...facet,
        value: null,
      });
    }

    return facets.value;
  }

  function clearValue({ code }: { code: string }): Facets {
    const facet = facets.value.get(code) as AllFacets;
    if (isPriceProps(facet)) {
      return facets.value.set(code, {
        ...facet,
        value: [0, Number(facet.options[facet.options.length - 1])],
      });
    }

    if (isSelectProps(facet)) {
      return facets.value.set(code, {
        ...facet,
        value: [],
      });
    }

    if (isToggleProps(facet)) {
      return facets.value.set(code, {
        ...facet,
        value: null,
      });
    }

    return facets.value;
  }

  function clearAll() {
    facets.value.forEach((facet, code) => {
      clearValue({ code });
    });
  }

  function setFacetValue({ code, value }: { code: string; value: AggregationOption }) {
    facets.value = updateFacetValue(facets.value)({ code, value });
  }

  function getFacetValues(code: string) {
    return facets.value.get(code);
  }

  return {
    facets,
    filters: productQueryFilters,
    setFacetValue,
    getFacetValues,
    removeItem,
    clearAll,
  };
}

/**
 * Maps an aggregation item
 */
export const mapAggregation =
  (facets: Facets) =>
  (
    /**
     * defining a third optional Parameter to adjust the facet aggregation option to some specific value
     */
    defaultOptions?: Record<string, AggregationOption[] | number[] | undefined>,
    queryParams?: LocationQuery,
  ) =>
  (apiItem: Aggregation): AllFacets => {
    const type = resolveFacetType(apiItem);
    const options = toNonNullable(apiItem.options);

    if (type === FacetType.Select) {
      const result: AllFacets = {
        // set label from map else set it to item's label
        label: apiItem.label?.toString() ?? apiItem.attribute_code,
        type: type,
        code: apiItem.attribute_code,
        options: apiItem.options?.filter((option): option is AggregationOption => !!option?.value) ?? [],
        value: Object.prototype.hasOwnProperty.call(queryParams, apiItem.attribute_code)
          ? (apiItem?.options?.filter(
              (option): option is AggregationOption =>
                !!queryParams?.[apiItem.attribute_code]?.toString()?.includes(option?.value ?? ''),
            ) ?? [])
          : ((facets?.get(apiItem.attribute_code) as ISelectFacet)?.value ?? []),
      };

      return result;
    }

    if (type === FacetType.Price) {
      const result: AllFacets = {
        // set label from map else set it to item's label
        label: apiItem.label?.toString() ?? apiItem.attribute_code,
        type,
        code: apiItem.attribute_code,
        options: [],
        value:
          Object.prototype.hasOwnProperty.call(queryParams, 'price_from') &&
          Object.prototype.hasOwnProperty.call(queryParams, 'price_to')
            ? [Number(queryParams?.price_from), Number(queryParams?.price_to)]
            : ((facets?.get(apiItem.attribute_code) as IPriceFacet)?.value ?? [
                0,
                Number(lastItem(options).value.split('_')[1]),
              ]),
      };
      // if we have a price attribute
      // set the options range to be from  0 to the last boundary in the last array element
      result.options = defaultOptions?.price
        ? [0, Number(lastItem(defaultOptions?.price as number[]))]
        : [0, Number(lastItem(options).value.split('_')[1])];

      return result;
    }

    if (type === FacetType.Toggle && options?.[0]) {
      const result: AllFacets = {
        // set label from map else set it to item's label
        label: apiItem.label?.toString() ?? apiItem.attribute_code,
        type,
        code: apiItem.attribute_code,
        options: options?.[0],
        value:
          Object.prototype.hasOwnProperty.call(queryParams, apiItem.attribute_code) &&
          queryParams?.[apiItem.attribute_code] === '1'
            ? options?.[0]
            : ((facets?.get(apiItem.attribute_code) as IToggleFacet)?.value ?? null),
      };

      return result;
    }

    return {
      // set label from map else set it to item's label
      label: apiItem.label?.toString() ?? apiItem.attribute_code,
      type: FacetType.Hidden,
      code: apiItem.attribute_code,
    } as IHiddenFacet;
  };

/**
 * called when mapping the facet items in mapFacet
 * handles setting the type attribute for each facet based on facet's attribute
 * @param {attribute: String, label: String, count: number, options: Array} apiItem
 */
function resolveFacetType(apiItem: Aggregation): FacetType {
  if (apiItem.attribute_code === 'price') {
    return FacetType.Price;
  }

  if (apiItem?.options?.length === 1 && ['1', '0', 'yes', 'no'].includes(apiItem?.options?.[0]?.value || '')) {
    return FacetType.Toggle;
  }

  if (isEqual(apiItem?.options?.map(option => option?.value).sort(), ['0', '1'])) {
    return FacetType.Toggle;
  }

  return FacetType.Select;
}

export const isFilterableFacet = (facet: AllFacets) => {
  if (isPriceProps(facet)) {
    const from = facet.value?.[0];
    const to = facet.value?.[1];

    if (from !== facet.options[0] || to !== facet.options[1]) {
      return true;
    }
    return false;
  }
  return facet.code !== 'price' && (facet?.value as AggregationOption[])?.length;
};

function fromFacetToUrlParams(facets: Facets) {
  const facetsArray = Array.from(facets.values());
  const initialRecord: Record<string, number | string | string[]> = {};
  return facetsArray.reduce((acc, facet) => {
    if (!facet) {
      return acc;
    }

    if (isPriceProps(facet)) {
      const from = facet.value?.[0];
      const to = facet.value?.[1];

      if (from !== facet.options[0] || to !== facet.options[1]) {
        acc.price_from = from ?? 0;
        acc.price_to = to ?? 0;
      }

      return acc;
    }

    if (Array.isArray(facet.value) && facet.value.length === 0) {
      delete acc[facet.code];
      return acc;
    }

    if (isToggleProps(facet)) {
      if (!facet.value) {
        delete acc[facet.code];
        return acc;
      }
      acc[facet.code] = '1';
      return acc;
    }

    if (!Array.isArray(facet.value) && !facet.value?.value) {
      return acc;
    }

    acc[facet.code] = Array.isArray(facet.value) ? facet.value.map<string>(option => option.value) : facet.value?.value;

    return acc;
  }, initialRecord);
}

export const updateFacetValue =
  (facets: Facets) =>
  ({ code, value }: { code: string; value: AggregationOption | AggregationOption[] | number[] | null }): Facets => {
    if (!facets.has(code)) {
      return facets;
    }

    const facet = facets.get(code) as AllFacets;

    if (isPriceProps(facet) && value && !isArrayOfNumbers(value)) {
      throw new Error('Price facet value must be an array of numbers');
    }

    if (isPriceProps(facet) && value && isArrayOfNumbers(value)) {
      return facets.set(code, {
        ...facet,
        value: value,
      });
    }

    if (isSelectProps(facet) && value && !isAggregationOptionArray(value)) {
      throw new Error('Select facet value must be an array of AggregationOption');
    }

    if (isSelectProps(facet) && value && isAggregationOptionArray(value)) {
      return facets.set(code, {
        ...facet,
        value: value,
      });
    }

    if (isToggleProps(facet) && value && isAggregationOption(value)) {
      return facets.set(code, {
        ...facet,
        value: value,
      });
    }

    return facets;
  };

export const removeFacetValue =
  (facets: Facets) =>
  ({ code }: { code: string }): Facets => {
    if (!facets.has(code)) {
      return facets;
    }

    const facet = facets.get(code) as AllFacets;

    if (isPriceProps(facet)) {
      return facets;
    }

    if (isSelectProps(facet)) {
      return facets.set(code, {
        ...facet,
        value: [],
      });
    }

    return facets;
  };

const isArrayOfNumbers = (value: AggregationOption | AggregationOption[] | number[]): value is number[] => {
  return Array.isArray(value) && value.every(val => typeof val === 'number');
};

const isAggregationOptionArray = (
  value: AggregationOption | AggregationOption[] | number[],
): value is AggregationOption[] => {
  return Array.isArray(value) && value.every(val => typeof val === 'object');
};

export const isAggregationOption = (
  value: AggregationOption | AggregationOption[] | number[] | null | undefined,
): value is AggregationOption => {
  return !!value && typeof value === 'object';
};

// valid price show tag if the value is not empty and the length is 2 and the values are not equal to the default values
export const isValidPriceValue = (priceFacet: IPriceFacet): boolean => {
  if (priceFacet.value?.every(price => price === 0)) {
    return false;
  }
  return priceFacet.value?.length === 2 && priceFacet.value[1] !== priceFacet.options[1];
};
